import { useCallback, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { ScheduleAvailabilityByDayOfWeek } from '@frontend/api-schedule-availability';
import { ExceptionUtils, ExceptionTypes, ExceptionQueries } from '@frontend/api-schedule-exceptions';
import { ServiceProvidersApi, ServiceProvidersQueries, ServiceProvidersTypes } from '@frontend/api-service-providers';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useQuery } from '@frontend/react-query-helpers';
import { breakpoints } from '@frontend/responsiveness';
import { ScheduleAvailabilityHours, ScheduleAvailabilityHoursValueRef } from '@frontend/schedule-availability-hours';
import { theme } from '@frontend/theme';
import {
  FullscreenModal,
  ModalControlModalProps,
  ButtonBar,
  Alert,
  Tabs,
  Text,
  useAlert,
  Button,
  useModalControl,
  ConfirmationModal,
} from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { scheduleDirectAvailabilityBannerText } from '../../constants';
import { getSanitizedScheduleHoursData } from '../../utils';
import { DirectAvailabilityCheck } from '../DirectAvailabilityCheck';
import { ScheduleBanner } from '../ScheduleBanner';
import { ScheduleExceptions } from '../ScheduleExceptions';
import * as styles from './styles';

const { providerScheduleDirectAvailabilityTitle, providerScheduleDirectAvailabilityMessage } =
  scheduleDirectAvailabilityBannerText;

const scheduleHasValues = (schedule: ScheduleAvailabilityByDayOfWeek) => {
  const values = schedule ? Object.values(schedule) : [];
  const hasValues = values?.filter((value) => {
    return value.length > 0;
  });
  return hasValues.length;
};

enum ProviderTabs {
  ProviderSchedule = 'provider-schedule',
  ProviderExceptions = 'provider-exceptions',
}

interface Props extends ModalControlModalProps {
  selectedProvider?: Partial<ServiceProvidersTypes.ServiceProvider>;
  primaryActionName: string;
  secondaryActionName: string;
  handleCancel: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  locationId: string;
}
export function ProviderAvailabilityModal({
  selectedProvider,
  primaryActionName,
  secondaryActionName,
  handleCancel,
  locationId,
  ...providerAvailabilityModalProps
}: Props) {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { defaultFilterObject, hasConflicts } = ExceptionUtils;
  const unsavedChangesModalControls = useModalControl();
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [formState, setFormState] = useState<{ hasError: boolean; hasChanges: boolean }>({
    hasError: false,
    hasChanges: false,
  });
  const availabilityHoursRef = useRef<ScheduleAvailabilityHoursValueRef>();
  const handleAvailabilityHoursChange = useCallback(
    (hasChanges: boolean, hasError: boolean) => setFormState({ hasChanges, hasError }),
    []
  );

  const [activeTab, setActiveTab] = useState(ProviderTabs.ProviderSchedule);

  const [filters, setFilters] = useState(defaultFilterObject);

  const handleFiltersUpdate = (newFilters: ExceptionTypes.ExceptionTableFilters) => {
    setFilters(newFilters);
  };

  const { startDateTime, endDateTime } = useMemo(() => {
    return {
      startDateTime: filters.dateRange[0] && dayjs(`${filters.dateRange[0]} ${filters.timeRange[0]}`).format(),
      endDateTime: filters.dateRange[1] && dayjs(`${filters.dateRange[1]} ${filters.timeRange[1]}`).format(),
    };
  }, [filters]);

  const filterByDateTime = () => {
    if (activeTab === 'provider-exceptions') {
      if (!filters.timeRange[0].length || filters.isAllDay) {
        setFilters({ ...filters, timeRange: ['00:00:00', '11:59:00'] });
      }
    }
  };

  useMemo(async () => {
    await filterByDateTime();
    refetchAvailableExceptions();
    refetchUnAvailableExceptions();
  }, [filters.dateRange[0]]);

  const {
    data: providerHours,
    isError: isErrorProviderHours,
    refetch: refetchProviderHours,
    isLoading: isLoadingProviderHours,
  } = useQuery({
    enabled: !!selectedProvider?.id,
    queryFn: async () =>
      await ServiceProvidersApi.getServiceProviderHours(selectedProvider?.id ?? '', selectedProvider?.locationId || ''),
    queryKey: queryKeys.providerUnavailability(selectedProvider?.id ?? '', selectedProvider?.locationId || ''),
    retry: 1,
  });

  const { data: unAvailableExceptionData, refetch: refetchUnAvailableExceptions } =
    ServiceProvidersQueries.useGetProvidersExceptionsForMultipleProvidersOfMultiLocations({
      providerIds: [selectedProvider?.id || ''],
      locationIds: [locationId],
      startDateTime,
      endDateTime,
      isAvailable: false,
    });

  // Available type exceptions are not fetched unless isAvailable is send explicitly as param, so we need to combine fetch and combine both to show all exceptions in table
  // This can be removed when API is updated to return all exceptions
  const {
    data: availableExceptionData,
    isLoading: isLoadingAvailableExceptions,
    refetch: refetchAvailableExceptions,
  } = ServiceProvidersQueries.useGetProvidersExceptionsForMultipleProvidersOfMultiLocations({
    providerIds: [selectedProvider?.id || ''],
    locationIds: [locationId],
    startDateTime,
    endDateTime,
    isAvailable: true,
  });

  const exceptionData = [...(unAvailableExceptionData?.data || []), ...(availableExceptionData?.data || [])].sort(
    (a, b) => dayjs(a.startDateTime).diff(dayjs(b.startDateTime))
  );

  const { data: officeExceptionData } = ExceptionQueries.useGetOfficeExceptions({
    locationIds: [locationId],
    startDateTime,
    endDateTime,
    isAvailable: false,
  });

  const sanitizedProviderHours = useMemo(() => {
    return providerHours ? getSanitizedScheduleHoursData(providerHours) : {};
  }, [providerHours]);

  const handleSave = async () => {
    try {
      setIsSavingSchedule(true);
      const updatedScheduleAvailability = availabilityHoursRef.current?.getUpdatedScheduleInfo();
      if (selectedProvider?.id && formState.hasChanges && updatedScheduleAvailability) {
        await ServiceProvidersApi.updateServiceProviderHours(
          selectedProvider.id,
          locationId,
          updatedScheduleAvailability
        );
        alert.success(t('Successfully updated schedule'));
        refetchProviderHours();
        providerAvailabilityModalProps.onClose();
      }
    } catch {
      alert.error(t('Failed to update schedule'));
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const checkConflicts = (newException: ExceptionTypes.ExceptionForm) => {
    if (!officeExceptionData?.data) {
      return;
    }
    return hasConflicts(officeExceptionData?.data as ExceptionTypes.Exception[], newException);
  };

  const title = useMemo(() => {
    return t(`Edit Schedule for {{providerName}}`, {
      providerName: `${selectedProvider?.firstName} ${selectedProvider?.lastName}`,
    });
  }, [selectedProvider]);

  const hasValues = useMemo(() => {
    if (sanitizedProviderHours) {
      return scheduleHasValues(sanitizedProviderHours);
    }
    return;
  }, [sanitizedProviderHours]);

  const handleSetProviderScheduleClick = () => {
    setActiveTab(ProviderTabs.ProviderSchedule);
  };

  const shouldDisableSave = formState.hasError || !formState.hasChanges;

  return (
    <>
      <FullscreenModal
        {...providerAvailabilityModalProps}
        onClose={() =>
          formState.hasChanges ? unsavedChangesModalControls.openModal() : providerAvailabilityModalProps.onClose()
        }
        mountTarget='#vertical-slide-modal-backdrop'
        css={internalStyles.fullscreenModal}
      >
        <FullscreenModal.Header title={title} css={styles.fullscreenModalHeader} />
        <FullscreenModal.Body css={styles.modalBody}>
          {isErrorProviderHours && (
            <Alert type='error'>{t('Unable to fetch provider schedule. Please try again later.')}</Alert>
          )}
          <DirectAvailabilityCheck locationId={selectedProvider?.locationId || locationId || ''}>
            <ScheduleBanner
              title={providerScheduleDirectAvailabilityTitle}
              message={providerScheduleDirectAvailabilityMessage}
            />
          </DirectAvailabilityCheck>
          <Tabs initialTab={activeTab} onChange={(e) => setActiveTab(e as ProviderTabs)} controlledTab={activeTab}>
            <Tabs.Bar>
              <Tabs.Tab id={ProviderTabs.ProviderSchedule}>{t('Provider schedule')}</Tabs.Tab>
              <Tabs.Tab id={ProviderTabs.ProviderExceptions}>{t('Exceptions')}</Tabs.Tab>
            </Tabs.Bar>
            <Tabs.Panel controller={ProviderTabs.ProviderSchedule} keepMounted={false}>
              <div css={{ padding: theme.spacing(2, 0), maxWidth: breakpoints.medium.min }}>
                <ScheduleAvailabilityHours
                  closedOfficeText={t('Out of Office')}
                  onChange={handleAvailabilityHoursChange}
                  isLoading={isLoadingProviderHours || isSavingSchedule}
                  // Reset initial schedule availability if modal is shown
                  scheduleInfo={providerAvailabilityModalProps.show ? sanitizedProviderHours : {}}
                  ref={availabilityHoursRef}
                />
                <ButtonBar css={{ marginTop: theme.spacing(4), gap: theme.spacing(2), paddingBottom: 0 }}>
                  <Button variant='secondary' size='large' css={{ flexGrow: 1 }} onClick={(e) => handleCancel(e)}>
                    {secondaryActionName}
                  </Button>
                  <Button
                    size='large'
                    loading={isSavingSchedule}
                    disabled={shouldDisableSave}
                    css={{ flexGrow: 1 }}
                    onClick={handleSave}
                  >
                    {primaryActionName}
                  </Button>
                </ButtonBar>
              </div>
            </Tabs.Panel>
            <Tabs.Panel controller={ProviderTabs.ProviderExceptions} keepMounted={false}>
              <Page css={styles.page} maxWidth={'100%'}>
                {hasValues ? (
                  <div>
                    <Text css={styles.subtitle}>
                      {t(
                        "Any exceptions added here will only affect this provider's schedule, it will not affect your office schedule."
                      )}
                    </Text>
                    <ScheduleExceptions
                      locationId={locationId}
                      data={exceptionData as ExceptionTypes.Exception[]}
                      isLoading={isLoadingAvailableExceptions}
                      providerId={selectedProvider?.id}
                      filters={filters}
                      handleFiltersUpdate={handleFiltersUpdate}
                      checkConflicts={checkConflicts}
                    />
                  </div>
                ) : (
                  <>
                    <Text css={styles.subtitle}>
                      {t('Schedules must be set before Exceptions to provide a base availability.')}
                    </Text>
                    <Button css={styles.singleButton} onClick={handleSetProviderScheduleClick}>
                      {t('Set Provider Schedule')}
                    </Button>
                  </>
                )}
              </Page>
            </Tabs.Panel>
          </Tabs>
        </FullscreenModal.Body>
      </FullscreenModal>
      <ConfirmationModal
        {...unsavedChangesModalControls.modalProps}
        title={t('Unsaved Changes')}
        destructive
        message={t('Your changes will not be saved. Are you sure you want to leave this page?')}
        confirmLabel={t('Leave')}
        cancelLabel={t('Stay')}
        onCancel={unsavedChangesModalControls.closeModal}
        onConfirm={providerAvailabilityModalProps.onClose}
      />
    </>
  );
}

const internalStyles = {
  fullscreenModal: css({
    containerType: 'inline-size',
  }),
};
